import type { Communication, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import {
  getConversationCounterpartyDisplay,
  needsParticipantDisplayResolution,
  resolveConversationCounterpartyDisplay,
} from '../services/messagingFhir';

/**
 * Resolves a thread counterparty name for sidebar/header display.
 * Falls back to a Patient/Practitioner read when stored refs lack a usable display.
 */
export function useResolvedCounterpartyDisplay(
  thread: Communication | undefined,
  currentUserReference: string | undefined,
  groupMembers?: Reference[],
): string {
  const medplum = useMedplum();
  const memberKey = groupMembers?.map((m) => m.reference).join('|') ?? '';

  const syncLabel = useMemo(() => {
    if (!thread) return 'Unknown';
    return getConversationCounterpartyDisplay(thread, currentUserReference, groupMembers);
  }, [thread, currentUserReference, memberKey, groupMembers]);

  const [label, setLabel] = useState(syncLabel);

  useEffect(() => {
    setLabel(syncLabel);
    if (!thread || !needsParticipantDisplayResolution(syncLabel)) return;

    let cancelled = false;
    void resolveConversationCounterpartyDisplay(
      medplum,
      thread,
      currentUserReference,
      groupMembers,
    ).then((resolved) => {
      if (!cancelled) setLabel(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [medplum, thread, currentUserReference, memberKey, syncLabel, groupMembers]);

  return label;
}
